import React, { useState, useRef, useEffect } from 'react';
import { stylistAPI } from '../../../../services/api';
import { X, MessageSquare } from 'lucide-react';

const AddRecommendationModal = ({ clientId, onClose, onAdded, requests = [] }) => {
  const [formData, setFormData] = useState({
    product_url: '',
    description: '',
    image_url: '',
    request_id: null
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [availableRequests, setAvailableRequests] = useState(requests);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Fetch requests if not provided
    if (requests.length === 0) {
      stylistAPI.getClientRequests(clientId).then(setAvailableRequests).catch(console.error);
    }
  }, [clientId, requests]);

  // Filter only shopping-type requests that are not completed
  const shoppingRequests = availableRequests.filter(r => r.request_type === 'shopping' && r.status !== 'completed');

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await stylistAPI.uploadImage(file);
      const fullUrl = response.url;
      setFormData({ ...formData, image_url: fullUrl });
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_url || !formData.description) {
      alert('Заполните обязательные поля');
      return;
    }

    setLoading(true);
    try {
      await stylistAPI.addRecommendation(clientId, {
        ...formData,
        client_id: clientId,
        request_id: formData.request_id || null
      });
      
      // If linked to a request, update request status to completed
      if (formData.request_id) {
        try {
          await stylistAPI.updateRequestStatus(formData.request_id, 'completed');
        } catch (err) {
          console.error('Error updating request status:', err);
        }
      }
      
      const recs = await stylistAPI.getRecommendations(clientId);
      onAdded(recs[0]);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-l)', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'var(--white)', padding: 'var(--space-xxl)', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-xl)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem' }}>Добавить рекомендацию</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Ссылка на товар *
            </label>
            <input
              type="url"
              value={formData.product_url}
              onChange={(e) => setFormData({ ...formData, product_url: e.target.value })}
              placeholder="https://..."
              style={{ width: '100%', padding: '12px', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', border: '1px solid var(--soft-gray)', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Описание *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              placeholder="Белая блузка для офиса..."
              style={{ width: '100%', padding: '12px', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', border: '1px solid var(--soft-gray)', boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Фото (опционально)
            </label>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{ display: 'none' }} />
            {formData.image_url ? (
              <div style={{ position: 'relative', width: '100px', height: '100px' }}>
                <img src={formData.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button type="button" onClick={() => setFormData({ ...formData, image_url: '' })} style={{ position: 'absolute', top: '4px', right: '4px', padding: '4px', backgroundColor: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ padding: '10px 16px', backgroundColor: 'var(--cream)', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.8125rem' }}>
                {uploading ? 'Загрузка...' : 'Загрузить фото'}
              </button>
            )}
          </div>

          {/* Link to Request */}
          {shoppingRequests.length > 0 && (
            <div style={{ marginBottom: 'var(--space-l)' }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
                Привязать к запросу клиента
              </label>
              <select
                value={formData.request_id || ''}
                onChange={(e) => setFormData({ ...formData, request_id: e.target.value || null })}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.9375rem',
                  border: '1px solid var(--soft-gray)',
                  backgroundColor: 'var(--white)',
                  cursor: 'pointer'
                }}
              >
                <option value="">— Не привязывать —</option>
                {shoppingRequests.map((req) => (
                  <option key={req.id} value={req.id}>
                    {req.description.substring(0, 60)}{req.description.length > 60 ? '...' : ''} 
                    ({req.status === 'new' ? 'Новый' : 'В работе'})
                  </option>
                ))}
              </select>
              {formData.request_id && (
                <div style={{
                  marginTop: 'var(--space-s)',
                  padding: 'var(--space-s) var(--space-m)',
                  backgroundColor: '#22c55e15',
                  borderLeft: '3px solid #22c55e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-s)'
                }}>
                  <MessageSquare size={14} style={{ color: '#22c55e' }} />
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: '#15803d' }}>
                    После добавления запрос будет отмечен как выполненный
                  </span>
                </div>
              )}
            </div>
          )}

          <button type="submit" disabled={loading} style={{ width: '100%', padding: '16px', backgroundColor: 'var(--black)', color: 'var(--white)', border: 'none', fontFamily: 'var(--font-body)', fontSize: '0.8125rem', letterSpacing: '1px', textTransform: 'uppercase', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Добавление...' : 'Добавить'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddRecommendationModal;
